import React, { useEffect, useMemo, useState } from "react";
import { Tabs, Button, Row } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAuthInfo } from "../Auth/authSelectors";
import { accessChecker } from "../../lib/functions";
import DeliveryDashboard from "./components/DeliveryDashboard";
import DeliveryJobs from "./components/DeliveryJobs";
import DeliveryRoutes from "./components/DeliveryRoutes";
import DeliveryUsers from "./components/DeliveryUsers";
import DeliveryRoutePoints from "./components/DeliveryRoutePoints";
import DeliveryAreas from "./components/DeliveryAreas";
import DeliveryAssignments from "./components/DeliveryAssignments";
import DeliveryLiveTracking from "./components/DeliveryLiveTracking";
import DeliveryStatusFlow from "./components/DeliveryStatusFlow";
import DeliveryPickup from "./components/DeliveryPickup";
import { selectDeliveryUsers } from "./deliverySelectors";
import { fetchDeliveryUsers } from "./deliveryThunks";
import DeliveryExecution from "./components/DeliveryExecution/DeliveryExecution";
import { selectEmployees, selectHrLocation } from "../AttendanceOverview/attendanceOverviewSelectors";
import { fetchHrLocation } from "../AttendanceOverview/attendanceOverviewThunk";

const TAB_PATHS = {
  dashboard: "/delivery/dashboard",
  "delivery-execution": "/delivery/delivery-execution",
  jobs: "/delivery/jobs",
  routes: "/delivery/routes",
  "route-points": "/delivery/route-points",
  areas: "/delivery/areas",
  assignments: "/delivery/assignments",
  "live-tracking": "/delivery/live-tracking",
  "status-flow": "/delivery/status-flow",
  pickup: "/delivery/pickup",
  users: "/delivery/users",
};

const DeliveryModule = ({ defaultTab = "dashboard" }) => {
  const dispatch = useDispatch();
  const authInfo = useSelector(selectAuthInfo);
  const [isAdminUser, isAdminUserSet] = useState(false);
  const [canExecuteDeliveryRoute, canExecuteDeliveryRouteSet] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const navigate = useNavigate();
  const deliveryUsers = useSelector(selectDeliveryUsers);
  const isEmployee =
    authInfo?.employee_id &&
    authInfo.employee_id !== 0;
  const rawEmployees = useSelector(selectEmployees);
  const hrLocation = useSelector(selectHrLocation);
  const canManageRoutes = accessChecker("delivery_routes_manage");

  useEffect(() => {
    const hrRoles = [
      // HR role
      "ForthERP_Basic_hr",
      "ForthERP_Standard_hr",
      "ForthERP_Pro_hr",
      "PharmaERP_Basic_hr",
      "PharmaERP_Standard_hr",
      "PharmaERP_Pro_hr",
      "PharmaBooks_Basic_hr",
      "PharmaBooks_Standard_hr",
      "PharmaBooks_Pro_hr",
      "ForthBooks_Basic_hr",
      "ForthBooks_Standard_hr",
      "ForthBooks_Pro_hr",
      "HRPayroll_Standard_hr",
      "HRPayroll_Pro_hr",
      "SuperERP_hr",
      'ForthCRM_Pro_hr',
      'ForthCRM_Standard_hr',

      // Admin role
      "ForthERP_Basic_admin",
      "ForthERP_Standard_admin",
      "ForthERP_Pro_admin",
      "PharmaERP_Basic_admin",
      "PharmaERP_Standard_admin",
      "PharmaERP_Pro_admin",
      "PharmaBooks_Basic_admin",
      "PharmaBooks_Standard_admin",
      "PharmaBooks_Pro_admin",
      "ForthBooks_Basic_admin",
      "ForthBooks_Standard_admin",
      "ForthBooks_Pro_admin",
      "HRPayroll_Standard_admin",
      "HRPayroll_Pro_admin",
      "SuperERP_admin",
      "SuperERP_super_admin",
      "ForthCRM_Standard_admin",
      "ForthCRM_Pro_admin",
    ];

    const userRoles = authInfo.userInfo.user_role_names
      ? authInfo.userInfo.user_role_names.split(",").map((role) => role.trim())
      : [];

    const isExecuteDeliveryRoute = userRoles.some((role) => {
      const normalizedRole = role.trim().toLowerCase();

      return (
        normalizedRole.endsWith("_delivery_agent") ||
        normalizedRole.endsWith("_delivery_admin")
      );
    });

    console.log(
      "USER ROLES:",
      authInfo?.userInfo?.user_role_names
    );

    const isAdminHR = userRoles.some((role) => hrRoles.includes(role));

    isAdminUserSet(isAdminHR);
    canExecuteDeliveryRouteSet(isExecuteDeliveryRoute);
  }, [authInfo]);

  const employees = rawEmployees.map((emp) => ({
    ...emp,
    employee_id: emp.id,
    employee_name: emp.user_name,
  }));

  useEffect(() => {
    dispatch(fetchDeliveryUsers());
    dispatch(fetchHrLocation());
  }, [dispatch]);

  useEffect(() => {
    if (
      hrLocation?.latitude &&
      hrLocation?.longitude
    ) {
      setMapCenter({
        lat: Number(hrLocation.latitude),
        lng: Number(hrLocation.longitude),
      });

      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setMapCenter({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        },

        () => {
          setMapCenter({
            lat: 20,
            lng: 0,
          });
        }
      );
    } else {
      setMapCenter({
        lat: 20,
        lng: 0,
      });
    }
  }, [hrLocation]);

  const items = useMemo(() => {
    const tabs = [
      {
        key: "dashboard",
        label: "Dashboard",
        children: <DeliveryDashboard authInfo={authInfo} />,
      },
      ...canExecuteDeliveryRoute ? [{
        key: "delivery-execution",
        label: "My Route",
        children: <DeliveryExecution
          center={mapCenter || { lat: 20, lng: 0, }}
          zoom={mapCenter ? 10 : 2}
        />,
      }] : [],
      {
        key: "jobs",
        label: "Jobs",
        children: <DeliveryJobs authInfo={authInfo} />,
      },
      {
        key: "pickup",
        label: "Pickup",
        children: <DeliveryPickup authInfo={authInfo} />,
      },
    ];

    if (canManageRoutes) {
      tabs.push(
        {
          key: "route-points",
          label: "Route Points",
          children: <DeliveryRoutePoints />,
        },
        {
          key: "areas",
          label: "Areas",
          children: <DeliveryAreas />,
        },
        {
          key: "routes",
          label: "Routes",
          children: <DeliveryRoutes authInfo={authInfo} />,
        },
        {
          key: "assignments",
          label: "Assignments",
          children: <DeliveryAssignments deliveryUsers={deliveryUsers} />,
        },
        {
          key: "live-tracking",
          label: "Live Tracking",
          children: <DeliveryLiveTracking deliveryUsers={deliveryUsers} />,
        },
        {
          key: "status-flow",
          label: "Status Flow",
          children: <DeliveryStatusFlow />,
        },
        {
          key: "users",
          label: "Delivery Users",
          children: <DeliveryUsers />,
        }
      );
    }

    return tabs;
  }, [authInfo, canManageRoutes, canExecuteDeliveryRoute, deliveryUsers, mapCenter]);

  return (
    <div style={{ padding: "0 4px" }}>
      <Row style={{ marginBottom: 10 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
          onClick={() => navigate(-1)}
          data-testid="delivery-back-button"
        />
      </Row>
      <Tabs
        activeKey={defaultTab}
        items={items}
        onChange={(key) => navigate(TAB_PATHS[key] || "/delivery/dashboard")}
      />
    </div>
  );
};

export default DeliveryModule;
